import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { CommercialDocument } from "../../shared/commercial-documents";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { ContentLoading } from "../components/ContentLoading";
import {
  getCommercialDocument,
  getCommercialHistory,
  restoreCommercialDocument,
} from "./commercial-documents-api";
import { CommercialDocumentPreview } from "./CommercialDocumentPreview";
import { downloadCommercialDocument } from "./download-commercial-document";

type HistoryProps = {
  slug: string;
  current: CommercialDocument;
  canWrite: boolean;
  onRestored: (record: CommercialDocument) => void;
  onBusy: (busy: boolean) => void;
};
export function CommercialDocumentHistory({
  slug,
  current,
  canWrite,
  onRestored,
  onBusy,
}: HistoryProps) {
  const [selected, setSelected] = useState(current.revision);
  const history = useHistory(slug, current);
  const version = useQuery({
    queryKey: ["ops", "commercial-version", slug, current.id, selected],
    queryFn: () => getCommercialDocument(slug, current.id, selected),
  });
  const restore = useMutation({
    onMutate: () => {
      onBusy(true);
    },
    onSettled: () => {
      onBusy(false);
    },
    mutationFn: () =>
      restoreCommercialDocument(slug, current.id, {
        expectedRevision: current.revision,
        revision: selected,
      }),
    onSuccess: ({ document }) => {
      onRestored(document);
    },
  });
  const download = useMutation({ mutationFn: downloadCommercialDocument });
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Each save keeps the full document, its author, and date. Restoring creates a new version.
      </p>
      {history.isPending ? <ContentLoading label="Loading version history" /> : null}
      {history.error ? <FieldError>{history.error.message}</FieldError> : null}
      <div className="grid items-start gap-4 md:grid-cols-[210px_minmax(0,1fr)]">
        <VersionList
          history={history}
          selected={selected}
          setSelected={setSelected}
          current={current}
        />
        <div className="min-w-0 space-y-4">
          {version.isPending ? <ContentLoading label="Loading saved version" /> : null}
          {version.error ? <FieldError>{version.error.message}</FieldError> : null}
          {version.data ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={download.isPending}
                  onClick={() => {
                    download.mutate(version.data.document);
                  }}
                >
                  Download this version
                </Button>
                {canWrite && selected < current.revision ? (
                  <Button
                    disabled={restore.isPending}
                    onClick={() => {
                      restore.mutate();
                    }}
                  >
                    Restore this version
                  </Button>
                ) : null}
              </div>
              <CommercialDocumentPreview document={version.data.document.document} />
            </>
          ) : null}
          {restore.error ? <FieldError>{restore.error.message}</FieldError> : null}
          {download.error ? <FieldError>{download.error.message}</FieldError> : null}
        </div>
      </div>
    </div>
  );
}

function VersionList({
  history,
  selected,
  setSelected,
  current,
}: {
  history: ReturnType<typeof useHistory>;
  selected: number;
  setSelected: (revision: number) => void;
  current: CommercialDocument;
}) {
  return (
    <>
      <nav aria-label="Saved versions" className="max-h-96 space-y-1 overflow-y-auto">
        {history.data?.pages
          .flatMap((batch) => batch.revisions)
          .map((revision) => (
            <Button
              key={revision.revision}
              variant={selected === revision.revision ? "secondary" : "ghost"}
              className="h-auto w-full justify-start whitespace-normal py-3 text-left"
              aria-current={selected === revision.revision ? "true" : undefined}
              onClick={() => {
                setSelected(revision.revision);
              }}
            >
              <span>
                <span className="block font-semibold">
                  Version {revision.revision}
                  {revision.revision === current.revision ? " · Current" : ""}
                </span>
                <span className="block text-xs">
                  {revision.author} · {new Date(revision.createdAt * 1000).toLocaleString()}
                </span>
                {revision.changeNote ? (
                  <span className="mt-1 block text-xs">{revision.changeNote}</span>
                ) : null}
              </span>
            </Button>
          ))}
        {history.hasNextPage ? (
          <Button
            variant="ghost"
            disabled={history.isFetchingNextPage}
            onClick={() => {
              void history.fetchNextPage();
            }}
          >
            Older versions
          </Button>
        ) : null}
      </nav>
    </>
  );
}
function useHistory(slug: string, current: CommercialDocument) {
  return useInfiniteQuery({
    queryKey: ["ops", "commercial-history", slug, current.id, current.revision],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => getCommercialHistory(slug, current.id, pageParam),
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
}
